import type { TTableRowSelection as RowSelectionState } from '@chocolate-soup-inc/cs-frontend-components';
import { ExtendedFAB, Icon, LoadingInner, styleClassNames, tableClassNames, TableInner, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { CountryName } from '../../components/coutryName/CountryName';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { FloatingActions } from '../../components/floatingActions/FloatingActions';
import { TablePage, TTablePageRef } from '../../components/tablePage/TablePage';
import { getReadableRelationshipType } from '../../entities/dependants/shared';
import { translateEventType } from '../../entities/events/shared';
import { isDependant } from '../../entities/recipients/shared';
import { TEventTypes, useCreateEventGiftMutation, useSkipEventMutation } from '../../generated/graphql';

import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import clsx from 'clsx';
import tableStyles from '../../components/tablePage/TablePage.module.scss';
import { useFilter } from '../../contexts/filters';
import { isAddressValid } from '../../entities/addresses/shared';
import { TEventType, useSearchEventsByRecipient, useUpcomingSubscriptionHomeEventsOLD } from '../../entities/events/queries';
import { calculatedShippingDate } from '../../entities/gifts/shared';
import { useInfiniteScrollQuery } from '../../hooks/InitinityScroll';
import { getSubRows, useEventsFilters } from './_shared';

export const HomeEvents = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const [includingNextDays, setIncludingNextDays] = useState<number>(60);

  const { data: paginatedData, error: paginatedError, loading: paginatedLoading, nextToken, fetchMore } = useUpcomingSubscriptionHomeEventsOLD({ includingNextDays });

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { sentinelRef } = useInfiniteScrollQuery({
    rootRef: tableContainerRef,
    loading: paginatedLoading,
    queries: [
      {
        fetchMore: fetchMore.workAnniversaryFetchMore,
        nextToken: nextToken.workanniversaryNextToken
      },
      {
        fetchMore: fetchMore.birthdayFetchMore,
        nextToken: nextToken.birthdayNextToken
      }
    ]
  });

  const paginatedEvents = useMemo(() => getSubRows(paginatedData), [paginatedData]);

  const {
    companies,
    companiesCounts,
    companyFilter,
    filterByCompany,
    setCompany,
    months,
    monthFilter,
    filterByMonth,
    setMonth,
    eventTypeFilter,
    setEventTypeFilter,
    filterByEventType,
    recipientNameFilter,
    setRecipientNameFilter,
    debouncedRecipientNameFilter,
    filterByRecipientName,
    clientManagers,
    clientManagerFilter,
    setClientManagerFilter,
    filterByClientManager
  } = useEventsFilters({ events: paginatedEvents });

  // Fast exact server-side search once a company + name are both present, instead of relying on
  // the client-side fuzzy filter over whatever pagination has loaded so far (see
  // useSearchHomeEventsByRecipient). Falls back to the old client-side filter below when no
  // company is selected yet -- byCompanyIdAndFirstName/LastName can't search "every company".
  const {
    isActive: isSearchingByName,
    data: searchResultsRaw,
    loading: searchLoading,
    error: searchError
  } = useSearchEventsByRecipient({
    companyId: companyFilter,
    recipientName: debouncedRecipientNameFilter,
    eventPage: 'home',
    types: ['workAnniversary', 'birthday'],
    includingNextDays
  });

  const searchResults = useMemo(() => getSubRows(searchResultsRaw), [searchResultsRaw]);

  const events = isSearchingByName ? searchResults : paginatedEvents;
  const loading = isSearchingByName ? searchLoading : paginatedLoading;
  const error = isSearchingByName ? searchError : paginatedError;

  const [selectedRows, setSelectedRows] = useState<string[]>();
  const [skipLoading, setSkipLoading] = useState<boolean>(false);
  const [createGiftsLoading, setCreateGiftsLoading] = useState<boolean>(false);

  const [skipEvent] = useSkipEventMutation();
  const [createEventGift] = useCreateEventGiftMutation();

  const tableRef = createRef<TTablePageRef>();

  const onRowSelectionChange = useCallback((rowSelection: RowSelectionState) => {
    console.log('selectedRows?:', rowSelection);
    setSelectedRows(Object.keys(rowSelection));
  }, []);

  const getRowId = useCallback((event: TEventType) => {
    return event.id;
  }, []);

  const skipEvents = useCallback(async () => {
    setSkipLoading(true);
    let recordId: string | undefined;
    try {
      for (const id of selectedRows || []) {
        const event = events.find((item) => item.id === id);
        if (!event) continue;
        recordId = event.id;
        await skipEvent({
          variables: { id: event.id, companyId: event.companyId, version: event._version }
        });
      }
      tableRef.current?.resetConfirmationModal();
      setSelectedRows(undefined);
    } catch (error) {
      console.error('Event skip failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error skipping the selected events.');
    } finally {
      setSkipLoading(false);
    }
  }, [events, selectedRows, skipEvent, tableRef]);

  const createGifts = useCallback(async () => {
    setCreateGiftsLoading(true);
    let recordId: string | undefined;
    try {
      const createdIds = new Set<string>();
      for (const id of selectedRows || []) {
        const event = events.find((item) => item.id === id);
        if (!event) continue;
        const group = event.subRows?.length ? event.id : undefined;
        for (const target of [...(event.subRows || []), event]) {
          if (createdIds.has(target.id)) continue;
          recordId = target.id;
          await createEventGift({
            variables: {
              eventId: target.id,
              companyId: target.companyId,
              eventVersion: target._version,
              group,
              shippingDate: calculatedShippingDate({
                date: target.eventDate,
                address: {
                  country: target.address?.country as string,
                  state: target.address?.state as string
                }
              })
            }
          });
          createdIds.add(target.id);
        }
      }
      tableRef.current?.resetConfirmationModal();
      setSelectedRows(undefined);
    } catch (error) {
      console.error('Event gift creation failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error creating the selected gifts.');
    } finally {
      setCreateGiftsLoading(false);
    }
  }, [createEventGift, events, selectedRows, tableRef]);

  const onSkipEventsClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Skip ${selectedRows?.length} Events?`,
      supportingText: `Are you sure you want to skip these ${selectedRows?.length} events?`,
      confirmLabel: 'Skip',
      onConfirmClick: skipEvents,
      open: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, skipEvents, tableRef, tableRef.current]);

  const onCreateGiftsClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Create ${selectedRows?.length} Gifts?`,
      supportingText: `Are you sure you want to create these ${selectedRows?.length} gifts?`,
      confirmLabel: 'Create',
      onConfirmClick: createGifts,
      open: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createGifts, selectedRows, tableRef, tableRef.current]);

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      type: 'singleSelect',
      label: 'Date',
      name: 'date',
      onChange: setMonth,
      options: months.map((m) => ({ label: m, value: m })),
      value: monthFilter
    });

    filtersList.push({
      disabled: companies.length === 0,
      type: 'singleSelect',
      label: 'Company',
      name: 'companyId',
      onChange: setCompany,
      options: companies?.map((c) => ({ label: `${c.name} (${companiesCounts[c.id]})`, value: c.id })),
      value: companyFilter
    });

    filtersList.push({
      type: 'textInput',
      label: 'Recipient Name',
      name: 'recipientName',
      onChange: (v) => {
        setRecipientNameFilter(v != null ? (v as string) : undefined);
      },
      value: recipientNameFilter
    });

    filtersList.push({
      disabled: clientManagers.length === 0,
      label: 'Client Manager',
      name: 'clientManager',
      onChange: (v) => {
        setClientManagerFilter(v != null ? (v as string) : undefined);
      },
      options: clientManagers.map((c) => ({ label: c.name, value: c.id })),
      type: 'singleSelect',
      value: clientManagerFilter
    });

    filtersList.push({
      type: 'singleSelect',
      label: 'Event Type',
      name: 'eventType',
      onChange: (v) => {
        setEventTypeFilter(v != null ? (v as TEventTypes) : undefined);
      },
      options: [
        {
          label: 'Birthday',
          value: TEventTypes.Birthday
        },
        {
          label: 'Work Anniversary',
          value: TEventTypes.WorkAnniversary
        }
      ],
      value: eventTypeFilter
    });

    filtersList.push({
      includeEmptyOption: false,
      type: 'singleSelect',
      label: 'Including Next Days',
      name: 'includingNextDays',
      onChange: (v) => {
        setIncludingNextDays(v != null ? Number(v) : 60);
      },
      options: [
        {
          label: '60 Days',
          value: 60
        },
        {
          label: '90 Days',
          value: 90
        },
        {
          label: 'All Days',
          value: 999
        }
      ],
      value: includingNextDays
    });

    return filtersList;
  }, [
    companies,
    companiesCounts,
    companyFilter,
    setCompany,
    monthFilter,
    months,
    setMonth,
    recipientNameFilter,
    setRecipientNameFilter,
    eventTypeFilter,
    setEventTypeFilter,
    includingNextDays,
    clientManagers,
    clientManagerFilter,
    setClientManagerFilter
  ]);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      return (
        filterByCompany(e) &&
        filterByMonth(e) &&
        filterByEventType(e) &&
        // The backend already matched by name when a company is selected -- reapplying the
        // client-side fuzzy filter on top could silently drop valid results whose fullName
        // scores below the quickScore threshold even though they matched the exact prefix
        // search server-side.
        (isSearchingByName || filterByRecipientName(e)) &&
        filterByClientManager(e)
      );
    });
  }, [filterByCompany, filterByEventType, filterByMonth, isSearchingByName, filterByRecipientName, filterByClientManager, events]);

  //console.log('flatEvents?:', data);
  //console.log('events?:', events);
  console.log('filteredEvents?:', filteredEvents);

  const getRowClassName = useCallback((row: Record<string, any>) => {
    if (!row.original.address || !row.original.recipient) return styleClassNames.attentionRow;
  }, []);

  return (
    <TablePage actionsLoading={createGiftsLoading || skipLoading} error={error} ref={tableRef} title='Upcoming Home Events'>
      <Filters filters={filters} />
      <div ref={tableContainerRef} style={{ overflow: 'auto' }}>
        <TableInner<TEventType>
          enableSorting={true}
          defaultSorting={{ id: 'shippingDate', desc: true }}
          data={filteredEvents}
          emptyText={isSearchingByName ? `No events found for "${debouncedRecipientNameFilter}".` : events.length === 0 ? 'No upcoming home events.' : 'No upcoming home events for the current filters.'}
          expandable={true}
          fillSelectedRows={true}
          fixedHeader={true}
          forceExpanded={true}
          getRowId={getRowId}
          getRowClassName={getRowClassName}
          hideSubRowsSelect={true}
          hoverableRows={!skipLoading && !createGiftsLoading}
          onRowSelectionChange={onRowSelectionChange}
          selectableRows={true}
          selectingChildSelectsFather={true}
          virtual={false}
          columns={[
            {
              header: 'Company Name',
              accessorKey: 'company.name',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                    {cell.row.original.wasPaused && (
                      <Tooltip message='This event was recently unpaused.'>
                        <Icon icon='play_circle' />
                      </Tooltip>
                    )}
                    <span>{cell.row.original.company?.name}</span>
                  </div>
                );
              }
            },
            {
              header: 'Recipient Name',
              accessorKey: 'recipient.fullName',
              cell: ({ cell }) => {
                let text = '';
                const pausePeriod = cell.row.original.employee?.pausePeriod;
                if (pausePeriod) {
                  const { fromDate, toDate } = pausePeriod;
                  if (fromDate) text += ` since ${readableDate(fromDate)}`;
                  if (toDate) text += ` to ${readableDate(toDate)}`;
                }
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                    {cell.row.original.employee?.isPaused && (
                      <Tooltip message={`Employee paused${text}.`}>
                        <Icon icon='pause_circle' />
                      </Tooltip>
                    )}
                    <span>{cell.row.original.recipient?.fullName}</span>
                  </div>
                );
              }
            },
            {
              header: 'Shipping Date',
              accessorKey: 'shippingDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.shippingDate ?? undefined)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Event Type',
              accessorKey: 'type',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.secondary)}>
                    <span>{translateEventType(cell.row.original.type)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Event Date',
              accessorKey: 'eventDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.eventDate)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Relationship',
              accessorKey: 'recipient.type',
              cell: ({ cell }) => {
                const recipient = cell.row.original.recipient;
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.tertiary)}>
                    <span>{recipient && isDependant(recipient) ? `${getReadableRelationshipType(recipient)} of ${recipient.employee?.fullName}` : 'Employee'}</span>
                  </div>
                );
              }
            },
            {
              header: 'Country',
              accessorKey: 'address.country',
              cell: ({ cell }) => {
                if (isAddressValid(cell.row.original.address))
                  return (
                    <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                      <CountryName name={cell.row.original.address?.country || undefined} />
                    </div>
                  );
                else
                  return (
                    <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                      <Icon icon='warning' />
                      <span>Missing address data</span>
                    </div>
                  );
              }
            }
          ]}
        />
        {loading && <LoadingInner className={clsx(tableStyles.contentLoader)} />}
        {!isSearchingByName && (nextToken.workanniversaryNextToken || nextToken.birthdayNextToken) && <div ref={sentinelRef} style={{ height: '20px' }} />}
      </div>
      {selectedRows && selectedRows.length > 0 && (
        <FloatingActions>
          <ExtendedFAB disabled={createGiftsLoading} label={`Skip ${selectedRows.length} event${selectedRows.length > 1 ? 's' : ''}`} leadingIcon='exit_to_app' loading={skipLoading} onClick={onSkipEventsClick} variant='tertiary' />
          <ExtendedFAB disabled={skipLoading} label={`Create ${selectedRows.length} gift${selectedRows.length > 1 ? 's' : ''}`} leadingIcon='add' loading={createGiftsLoading} onClick={onCreateGiftsClick} variant='surface' />
        </FloatingActions>
      )}
    </TablePage>
  );
};
